// Report-param grammar for a rapid-association block, ported from the coffee block, its chart view,
// its controls strip and the shared items list.
//
// Five params carry the block's state, addressed by prefix:
//   tcv  which of the three views the question is drawn as: bars, stacked bars or the scatter plot
//   cm   the chart's mode -- or the marker saying the word cloud is showing instead of a chart
//   z    the chart's zoom
//   s    the sort order
//   cb   which words are open in the stacked view, as a dot-joined list of positions
//
// `cm` is overloaded exactly as the answer-variants family overloads it, and the sort, collapse and
// cloud-marker rules are that family's -- they are imported rather than copied. What is this
// family's own is `tcv`, and the way the view chosen there decides what the chart may be drawn in.

import { isWcChartMode } from '@aytm/app/stats/page/question-views/answer-variants/lib/report-params'
import { PERCENT_CHART_MODES } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

export {
  calcCollapseSection,
  calcSortParamsSection,
  calcZoomSection,
  DEFAULT_SORT_ORDER,
  isWcChartMode,
  resolveChartMode,
  resolveSortOrder,
  resolveUncollapsed,
  WC_CHART_MODE
} from '@aytm/app/stats/page/question-views/answer-variants/lib/report-params'

export const DEFAULT_VIEW_MODE = 'bar'

export const VIEW_MODES = ['bar', 'stacked', 'scatter']

const SCATTER_CHART_MODE = 'scatter'

// A view the question cannot be drawn as is read as no view at all.
export const resolveViewMode = ({ tcv } = {}) => (VIEW_MODES.includes(tcv) ? tcv : DEFAULT_VIEW_MODE)

export const calcViewModeSection = viewMode => ({
  tcv: viewMode === DEFAULT_VIEW_MODE ? null : String(viewMode)
})

// Only the bar view has an axis to zoom, so leaving it drops the zoom with it.
export const hasZoom = viewMode => DEFAULT_VIEW_MODE === viewMode

// The scatter plot is its own mode and admits no other; the stacked view stacks shares, so it can
// only be drawn in percents. A question counting respondents has no pie to slice.
export const calcDefaultChartMode = ({
  chartMode,
  questionChartMode,
  globalChartMode,
  viewMode,
  percentsOnly
} = {}) => {
  let mode = 'off' === chartMode ? globalChartMode : chartMode
  mode = mode || questionChartMode || globalChartMode

  if ('pie' === mode) mode = 'respondents'

  if (SCATTER_CHART_MODE === viewMode) return SCATTER_CHART_MODE

  if ('stacked' === viewMode || true === percentsOnly) {
    return PERCENT_CHART_MODES.includes(mode) ? mode : 'percents'
  }

  return mode
}

// A chart mode that says what the question already says is not worth storing -- except when the
// question says "cloud" and the researcher wants a chart, which is what there is no silence for.
// Picking a view the chart mode cannot survive drops it, and the zoom with it.
export const calcChartParamsSection = ({ chartMode, questionChartMode, format, viewMode } = {}) => {
  const stored = format?.chartMode || questionChartMode || 'off'

  const section =
    isWcChartMode(stored) && !isWcChartMode(chartMode)
      ? { cm: 'off' }
      : { cm: chartMode === stored ? null : chartMode || null }

  if (isWcChartMode(chartMode)) return section

  return hasZoom(viewMode) ? section : { ...section, z: null }
}
